import _ from 'lodash';
import {useEffect, useRef} from 'react';

import {useDispatch, useMemoizedSelector} from '../../state/hooks';
import {type ProjectPageViewSpec} from '../../state/views/projectPage/types';
import {updateDefaultSpec} from '../../state/workspaces/actions';
import * as WorkspaceSelectors from '../../state/workspaces/selectors';
import {useInitWorkspaceAndHandleUrlChanges} from '../../state/workspaces/url';
import {useDetectChanges} from '../../util/react-help/detectReactPropChanges';

export type ViewType = 'project-view' | 'sweep-view' | 'group-view';
export type RecommendGrouping = 'group-and-jobtype' | 'jobtype';

export const useMultiRunWorkspaceProps = (args: {
  entityName: string;
  projectName: string;
  viewType: ViewType;
  defaultSpec: ProjectPageViewSpec;
  workspaceObjectID?: string;
}): WorkspaceSelectors.WorkspaceStatusOrData<
  WorkspaceSelectors.MultiRunWorkspacePageData &
    WorkspaceSelectors.GenericWorkspacePageData & {
      workspaceID: string;
    }
> => {
  const workspaceStateId = useInitWorkspaceAndHandleUrlChanges(args);
  const dispatch = useDispatch();

  useDetectChanges('useMultiRunWorkspaceProps', {
    workspaceStateId,
    argsViewType: args.viewType,
    args,
  });

  // useWorkspaceInit writes the default spec into redux exactly once, on
  // mount. The workspace templates query no longer blocks mount (see
  // useMultiRunWorkspaceQueryProps), so a template can resolve afterwards,
  // which changes the derived default spec passed in here. Without this sync
  // the late template would never reach redux, and creating a new workspace —
  // which reads workspace.defaultSpec from redux at creation time
  // (createNewWorkspaceAndLoad) — would silently ignore a template that had
  // already arrived. Compare by value so unrelated re-derivations (such as the
  // project query updating) don't cause repeated dispatches.
  const syncedDefaultSpecRef = useRef(args.defaultSpec);
  useEffect(() => {
    if (!_.isEqual(syncedDefaultSpecRef.current, args.defaultSpec)) {
      syncedDefaultSpecRef.current = args.defaultSpec;
      dispatch(updateDefaultSpec(workspaceStateId, args.defaultSpec));
    }
  }, [args.defaultSpec, workspaceStateId, dispatch]);

  const runPageData = useMemoizedSelector(
    WorkspaceSelectors.makeMultiRunWorkspacePageSelector,
    workspaceStateId,
    args.viewType
  );
  if (WorkspaceSelectors.getStatus(runPageData) !== 'ready') {
    return runPageData as WorkspaceSelectors.WorkspaceStatus;
  }

  if (!runPageData || !workspaceStateId) {
    console.error(
      'invalid state - workspace or workspaceID not set after loading'
    );
    return {
      status: 'error',
      error: {
        errorCode: '404',
        userTitle: 'Error loading workspace',
        userMessage: 'This workspace could not be loaded.',
      },
    };
  }

  return {...runPageData, workspaceID: workspaceStateId};
};
